// C.light 3D — motion system: Lenis smooth-scroll + GSAP ScrollTrigger + refraction-in reveals.
// Degrades gracefully: reveals + pointer-light + header work even if Lenis/GSAP fail to load.
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

// --- reveals: IntersectionObserver (scroll effect in real browsers) + timer failsafe (never leave content hidden) ---
const VH = () => document.documentElement.clientHeight || window.innerHeight || 800;
const io = new IntersectionObserver((es)=>{ es.forEach(e=>{ if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); } }); },
  { threshold:0.05, rootMargin:'0px 0px -2% 0px' });
function observeAll(){ document.querySelectorAll('[data-reveal]:not(.in)').forEach(el=>io.observe(el)); }
function scanReveals(){ const h=VH(); document.querySelectorAll('[data-reveal]:not(.in)').forEach(el=>{
  const r=el.getBoundingClientRect(); if(r.top < h*0.94 && r.bottom > -h*0.15) el.classList.add('in'); }); }
function revealAll(){ document.querySelectorAll('[data-reveal]:not(.in)').forEach(el=>el.classList.add('in')); }

// --- 4.1 stagger: 给每个 [data-reveal-group] 的直接子 [data-reveal] 写 --i(阶梯延迟由 CSS 消费) ---
// 在元素进场前就写好 --i；无论走 io / scanReveals / revealAll 哪条路径 .in 都能拿到正确 transition-delay。
function applyStagger(){
  document.querySelectorAll('[data-reveal-group]').forEach(group=>{
    let i=0;
    Array.prototype.forEach.call(group.children, (child)=>{
      if(child.matches && child.matches('[data-reveal]')){ child.style.setProperty('--i', i); i++; }
    });
  });
}
applyStagger();
observeAll();
addEventListener('scroll', scanReveals, {passive:true});
addEventListener('resize', scanReveals);
[250,700,1400,2400].forEach(t=>setTimeout(scanReveals,t));
// SAFETY NET: content must never stay invisible. Reveal everything shortly after load,
// so a failed IntersectionObserver / smooth-scroll / 0-height viewport can't hide the page.
addEventListener('load', ()=>{ applyStagger(); scanReveals(); setTimeout(scanReveals,400); setTimeout(revealAll,1600); });
setTimeout(revealAll, 3000);

// --- 4.3 Ken Burns: IO 进场 running / 离场 paused(省电)。与 reveal 观察器分离，需持续观察 ---
if(!reduce){
  const kbIo = new IntersectionObserver((es)=>{ es.forEach(e=>{
    e.target.style.animationPlayState = e.isIntersecting ? 'running' : 'paused';
  }); }, { threshold:0.05 });
  function observeKenburns(){ document.querySelectorAll('.kenburns').forEach(el=>kbIo.observe(el)); }
  observeKenburns();
  addEventListener('load', ()=>setTimeout(observeKenburns, 300)); // 迟到 DOM(shape 切换等)补挂
}

// --- pointer light field ---
addEventListener('pointermove', e=>{
  document.documentElement.style.setProperty('--lx', (e.clientX/innerWidth*100).toFixed(1)+'%');
  document.documentElement.style.setProperty('--ly', (e.clientY/innerHeight*100).toFixed(1)+'%');
}, {passive:true});

// --- header scrolled state + 4.6 滚动进度顶线(搭车 passive scroll) ---
const hdr = document.querySelector('.hdr');
// 顶部进度线：若页面未放 .scrollprog 则自动创建一次
let scrollprog = document.querySelector('.scrollprog');
if(!scrollprog){ scrollprog = document.createElement('div'); scrollprog.className = 'scrollprog';
  (document.body || document.documentElement).appendChild(scrollprog); }
const onScroll = ()=>{
  const y = window.scrollY || 0;
  if(hdr) hdr.classList.toggle('scrolled', y > 40);
  if(scrollprog){
    const max = (document.documentElement.scrollHeight - innerHeight);
    const p = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
    scrollprog.style.transform = 'scaleX(' + p.toFixed(4) + ')';
  }
};
addEventListener('scroll', onScroll, {passive:true}); onScroll();
addEventListener('resize', onScroll);

// --- mobile hamburger nav: delegated toggle of .hdr--open on the burger's own .hdr; auto-close on link tap ---
document.addEventListener('click', (e)=>{
  const burger = e.target.closest('.hdr__burger');
  if(burger){
    const header = burger.closest('.hdr');
    if(header){
      const open = header.classList.toggle('hdr--open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    return;
  }
  const navLink = e.target.closest('.hdr.hdr--open nav a');
  if(navLink){
    const header = navLink.closest('.hdr');
    if(header){
      header.classList.remove('hdr--open');
      const b = header.querySelector('.hdr__burger');
      if(b) b.setAttribute('aria-expanded','false');
    }
  }
});

// --- magnetic buttons ---
document.querySelectorAll('[data-magnetic]').forEach(el=>{
  el.addEventListener('pointermove', e=>{ const r=el.getBoundingClientRect();
    el.style.transform=`translate(${(e.clientX-r.left-r.width/2)*0.25}px,${(e.clientY-r.top-r.height/2)*0.3}px)`; });
  el.addEventListener('pointerleave', ()=>{ el.style.transform='translate(0,0)'; });
});

let lenis=null, gsap=null, ScrollTrigger=null;
(async ()=>{
  try{
    const [g, st, L] = await Promise.all([
      import('https://esm.sh/gsap@3.12.5'),
      import('https://esm.sh/gsap@3.12.5/ScrollTrigger'),
      import('https://esm.sh/lenis@1.1.14')
    ]);
    gsap = g.default || g; ScrollTrigger = st.default || st; const Lenis = L.default || L;
    gsap.registerPlugin(ScrollTrigger);
    window.gsap = gsap; window.ScrollTrigger = ScrollTrigger;
    if(!reduce){
      lenis = new Lenis({ lerp:0.1, wheelMultiplier:1 });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((t)=> lenis.raf(t*1000)); gsap.ticker.lagSmoothing(0);
      window.lenis = lenis;
    }
    document.dispatchEvent(new CustomEvent('motion:ready', { detail:{ gsap, ScrollTrigger, lenis } }));
  }catch(err){ console.warn('[motion] enhancement unavailable, using native scroll', err); }
})();

// (已移除 GSAP hero 揭幕:gsap.from(.crystal-stage,{opacity:0…}) 与 data-reveal 的 CSS
//  transition 抢 opacity,时序竞态会把整个水晶舞台钉在透明态 → 水晶"消失"。
//  data-reveal 本身已提供入场动画,揭幕属冗余,删除即修复。)

export { scanReveals };
